/**
 * blueprintMigrations — pure, idempotent load-time migrations for the Event
 * Graph. Runs at EVERY hydration boundary: shipped-JSON / cloud loads
 * (loadBlueprint.js applyBlueprintData), the editor's localStorage load
 * (BlueprintEditor.jsx loadEvents), file import, and collab remote-apply —
 * so no stale snapshot can put a retired node type back into the runtime.
 *
 * Deliberate leaf module (no imports): unit-testable under plain `node --test`
 * (tests/unit/blueprintMigrations.test.mjs) — same reasoning as
 * eventStarters.js / graphGroup.js.
 *
 * Current renames:
 *   - onAnimation -> onGesture   (server tag rename, long-standing)
 *   - beginPlay   -> onConstruct (Wave 2B "one node": the app is always
 *     playing now, so "fires once at play start" and "fires at graph
 *     (re)construction" collapse into the single onConstruct node. The
 *     avatar-loaded gating that beginPlay carried lives in the runtime
 *     dispatch — see onConstructDispatch.createConstructGate — not in the
 *     graph.)
 *
 * Renames are TYPE-only: ids, positions, data, edges all survive, and
 * duplicates are NOT merged — multiple starters of one type are legal
 * (startersFor fires all of them; `unique: true` only gates palette
 * insertion), so a graph that carried both a beginPlay and an onConstruct
 * ends up with two onConstruct starters, both firing.
 */

// The events localStorage key version, bumped v6 -> v7 for the
// beginPlay -> onConstruct rename (CLAUDE.md: any schema change bumps the
// key). loadBlueprint.getStorageKeysFor builds its keys from these constants;
// _OLD is the one-hop-behind migration source read by BlueprintEditor's
// loadEvents fallback.
export const EVENTS_STORAGE_VERSION = 'v7'
export const EVENTS_STORAGE_VERSION_OLD = 'v6'

const TYPE_RENAMES = {
  onAnimation: 'onGesture',
  beginPlay: 'onConstruct',
}

/**
 * Rename retired node types in place-preservingly: only nodes that actually
 * change are cloned (untouched nodes keep identity), and graphGroup
 * containers are recursed so a group's inner graph migrates too.
 */
export function migrateEventNodes(nodes) {
  return (nodes ?? []).map(n => {
    const nextType = TYPE_RENAMES[n.type]
    const renamed = nextType ? { ...n, type: nextType } : n
    if (renamed.type === 'graphGroup' && renamed.data?.innerNodes?.length) {
      const inner = migrateEventNodes(renamed.data.innerNodes)
      // Clone only when the recursion actually changed something.
      if (inner.some((m, i) => m !== renamed.data.innerNodes[i])) {
        return { ...renamed, data: { ...renamed.data, innerNodes: inner } }
      }
    }
    return renamed
  })
}

const CAMERA_ACTION_TYPES = new Set([
  'cameraAddYaw', 'cameraAddPitch', 'cameraAddDolly', 'cameraPanTarget',
])

/**
 * Take the RIGHT mouse button off the camera in ONE scope (the top-level graph,
 * or a graphGroup's inner graph — each has its own nodes+edges). Right-drag +
 * release is what browser/extension mouse-gesture software (Vivaldi/Opera
 * rockers, CrxMouse, …) turns into Back / Forward / open-new-tab, and a page
 * cannot suppress it — so a stored rig that pans on the right button is a hazard
 * regardless of what shipped as the default. Any right-drag that drives
 * cameraPanTarget moves to the MIDDLE button, and a now-redundant middle-drag
 * dolly (plus the dolly action node it exclusively drove) is dropped so the
 * middle button isn't overloaded — the wheel still dollies. Mirrors the same
 * change baked into conversational.json / cameraRigTemplates for new rigs.
 *
 * Pure and idempotent: after one pass no right-drag drives a pan, so a second
 * pass finds nothing and returns the input unchanged (changed:false).
 */
function migrateCameraScope(nodes, edges) {
  const ns = nodes ?? []
  const es = edges ?? []
  const byId = new Map(ns.map(n => [n.id, n]))
  const drives = (srcId, type) =>
    es.some(e => e.source === srcId && byId.get(e.target)?.type === type)

  const rightPanIds = new Set(ns
    .filter(n => n.type === 'onPointerDrag' && n.data?.button === 'right' && drives(n.id, 'cameraPanTarget'))
    .map(n => n.id))
  if (rightPanIds.size === 0) return { nodes: ns, edges: es, changed: false }

  // Middle-drag dollies to drop, freeing the middle button for pan.
  const dropIds = new Set()
  for (const n of ns) {
    if (n.type === 'onPointerDrag' && n.data?.button === 'middle' && drives(n.id, 'cameraAddDolly')) {
      dropIds.add(n.id)
    }
  }
  // Dolly ACTION nodes those middle-drags drove, if nothing surviving still drives them.
  for (const e of es) {
    if (!dropIds.has(e.source)) continue
    const tgt = byId.get(e.target)
    if (tgt?.type !== 'cameraAddDolly') continue
    const stillDriven = es.some(x => x.target === tgt.id && !dropIds.has(x.source))
    if (!stillDriven) dropIds.add(tgt.id)
  }

  const nextNodes = ns
    .filter(n => !dropIds.has(n.id))
    .map(n => rightPanIds.has(n.id) ? { ...n, data: { ...n.data, button: 'middle' } } : n)
  const nextEdges = es.filter(e => !dropIds.has(e.source) && !dropIds.has(e.target))
  return { nodes: nextNodes, edges: nextEdges, changed: true }
}

/**
 * Apply migrateCameraScope to the top-level graph AND to every graphGroup's
 * inner graph (the shipped camera rig lives inside a group). Returns updated
 * { nodes, edges }; identity is preserved where nothing changed.
 */
export function migrateCameraGraph(nodes, edges) {
  const withGroups = (nodes ?? []).map(n => {
    if (n.type === 'graphGroup' && n.data?.innerNodes) {
      const r = migrateCameraScope(n.data.innerNodes, n.data.innerEdges)
      if (r.changed) return { ...n, data: { ...n.data, innerNodes: r.nodes, innerEdges: r.edges } }
    }
    return n
  })
  const top = migrateCameraScope(withGroups, edges ?? [])
  return { nodes: top.nodes, edges: top.edges }
}

/**
 * Node renames + the camera right-button pass in one call, for loaders that
 * hold nodes and edges separately (BlueprintEditor's localStorage / collab /
 * import paths) rather than a whole snapshot. Returns { nodes, edges } — the
 * camera pass can drop both, so edges must be threaded through, not passed by
 * reference alongside a migrated node list.
 */
export function migrateEventNodesAndEdges(nodes, edges) {
  return migrateCameraGraph(migrateEventNodes(nodes), edges)
}

/**
 * Migrate a whole events snapshot ({ eventNodes, eventEdges, variables?,
 * functions? }). Pure — the input is never mutated. Two passes: type renames
 * (edges stay valid, so they pass through the rename), then the camera
 * right-button pass (which CAN drop nodes+edges, so it returns both).
 */
export function migrateEventGraph(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return snapshot
  const renamed = migrateEventNodes(snapshot.eventNodes)
  const cam = migrateCameraGraph(renamed, snapshot.eventEdges)
  return { ...snapshot, eventNodes: cam.nodes, eventEdges: cam.edges }
}
